import React, { ReactNode, useState, useEffect } from 'react';
import { 
  LightspeedError, 
  LightspeedErrorCodes, 
  LightspeedErrorHandler,
  isLightspeedErrorRetryable 
} from '../../utils/lightspeedErrorHandler';
import './lightspeed-components.scss';

interface LightspeedErrorNotificationProps {
  error: LightspeedError | null;
  onRetry?: () => void;
  onDismiss?: () => void;
  autoRetry?: boolean;
  maxRetries?: number;
  className?: string;
}

export const LightspeedErrorNotification: React.FC<LightspeedErrorNotificationProps> = ({
  error,
  onRetry,
  onDismiss,
  autoRetry = false,
  maxRetries = 3,
  className = ''
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryTimeout, setRetryTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (error && autoRetry && isLightspeedErrorRetryable(error) && retryCount < maxRetries && onRetry) {
      const delay = LightspeedErrorHandler.getRetryDelay(error, retryCount + 1);
      
      const timeout = setTimeout(() => {
        setIsRetrying(true);
        setRetryCount(prev => prev + 1);
        onRetry();
        setIsRetrying(false);
      }, delay);

      setRetryTimeout(timeout);

      return () => {
        if (timeout) clearTimeout(timeout);
      };
    }
  }, [error, autoRetry, retryCount, maxRetries, onRetry]);

  useEffect(() => {
    // Reset retry count when error changes
    if (error) {
      setRetryCount(0);
    }
  }, [error?.code]);

  const handleManualRetry = () => {
    if (onRetry && !isRetrying) {
      setIsRetrying(true);
      setRetryCount(prev => prev + 1);
      onRetry();
      setIsRetrying(false);
    }
  };

  const handleDismiss = () => {
    if (retryTimeout) {
      clearTimeout(retryTimeout);
      setRetryTimeout(null);
    }
    setRetryCount(0);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (!error) return null;

  const canRetry = isLightspeedErrorRetryable(error) && retryCount < maxRetries;
  const showRetryButton = canRetry && onRetry && !autoRetry;

  return (
    <div className={`lightspeed-error-notification ${getErrorTypeClass(error)} ${className}`}>
      <div className="error-content">
        <div className="error-icon">
          {getErrorIcon(error)}
        </div>
        
        <div className="error-message">
          <h4 className="error-title">
            {getErrorTitle(error)}
          </h4>
          <p className="error-description">
            {error.userMessage}
          </p>
          
          {retryCount > 0 && (
            <p className="retry-info">
              Attempt {retryCount} of {maxRetries}
            </p>
          )}
        </div>

        <div className="error-actions">
          {showRetryButton && (
            <button
              className="retry-button"
              onClick={handleManualRetry}
              disabled={isRetrying}
            >
              {isRetrying ? 'Retrying...' : 'Try Again'}
            </button>
          )}
          
          <button
            className="dismiss-button"
            onClick={handleDismiss}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      </div>

      {autoRetry && canRetry && (
        <div className="auto-retry-indicator">
          <div className="retry-progress">
            <span>Auto-retrying in a moment...</span>
            <div className="progress-bar">
              <div className="progress-fill"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getErrorTypeClass = (error: LightspeedError): string => {
  switch (error.code) {
    case LightspeedErrorCodes.OAUTH_ACCESS_DENIED:
    case LightspeedErrorCodes.API_INVALID_REQUEST:
      return 'error-oauth';
    case LightspeedErrorCodes.CONNECTION_FAILED:
    case LightspeedErrorCodes.CONNECTION_TIMEOUT:
      return 'error-connection';
    case LightspeedErrorCodes.API_RATE_LIMITED:
      return 'error-rate-limit';
    case LightspeedErrorCodes.NETWORK_ERROR:
      return 'error-network';
    default:
      return 'error-general';
  }
};

const getErrorIcon = (error: LightspeedError): ReactNode => {
  switch (error.code) {
    case LightspeedErrorCodes.OAUTH_ACCESS_DENIED:
    case LightspeedErrorCodes.API_INVALID_REQUEST:
      return <span className="error-icon error-oauth">🔐</span>;
    case LightspeedErrorCodes.CONNECTION_FAILED:
    case LightspeedErrorCodes.CONNECTION_TIMEOUT:
      return <span className="error-icon error-connection">🔌</span>;
    case LightspeedErrorCodes.API_RATE_LIMITED:
      return <span className="error-icon error-rate-limit">⏱️</span>;
    case LightspeedErrorCodes.NETWORK_ERROR:
      return <span className="error-icon error-network">🌐</span>;
    default:
      return <span className="error-icon error-general">⚠️</span>;
  }
};

const getErrorTitle = (error: LightspeedError): string => {
  if (error.code.includes('OAUTH')) return 'Authentication Error';
  if (error.code.includes('CONNECTION')) return 'Connection Error';
  if (error.code.includes('API_RATE_LIMITED')) return 'Rate Limited';
  if (error.code.includes('NETWORK')) return 'Network Error';
  if (error.code.includes('SERVER')) return 'Server Error';
  return 'Error';
};



export default LightspeedErrorNotification;